import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import clsx from 'clsx';

import { PINNED_SLIDE_MS } from './usePinnedSlideOpen';
import { clampPanelWidth } from './useResizablePanelWidth';

import styles from './PageSidebarHost.module.scss';

type PageSidebarHostContextValue = {
  reservePinnedContentSpace: boolean;
  setReservePinnedContentSpace: (reserved: boolean) => void;
  sidebarWidth?: number;
};

const PageSidebarHostContext =
  createContext<PageSidebarHostContextValue | null>(null);

export function usePageSidebarHost() {
  return useContext(PageSidebarHostContext);
}

export type PageSidebarHostProps = {
  children: ReactNode;
  className?: string;
  fullscreen?: boolean;
  open: boolean;
  pinned: boolean;
  scrollContained?: boolean;
  sidebar: ReactNode;
  sidebarWidth?: number;
};

export function PageSidebarHost({
  children,
  className,
  fullscreen = false,
  open,
  pinned,
  scrollContained = false,
  sidebar,
  sidebarWidth,
}: PageSidebarHostProps) {
  const [reservePinnedContentSpace, setReservePinnedContentSpace] = useState(
    open && pinned
  );

  useEffect(() => {
    if (!pinned) {
      setReservePinnedContentSpace(false);
      return;
    }

    if (open) {
      setReservePinnedContentSpace(true);
      return;
    }

    const timeout = globalThis.setTimeout(() => {
      setReservePinnedContentSpace(false);
    }, PINNED_SLIDE_MS);

    return () => globalThis.clearTimeout(timeout);
  }, [open, pinned]);

  const clampedSidebarWidth =
    sidebarWidth != null ? clampPanelWidth(sidebarWidth) : undefined;

  const hostContextValue = useMemo(
    () => ({
      reservePinnedContentSpace,
      setReservePinnedContentSpace,
      sidebarWidth: clampedSidebarWidth,
    }),
    [clampedSidebarWidth, reservePinnedContentSpace]
  );

  return (
    <PageSidebarHostContext.Provider value={hostContextValue}>
      <div
        className={clsx(
          styles['host'],
          {
            [styles['host--fullscreen']]: fullscreen,
            [styles['host--pinned']]: pinned,
            [styles['host--scroll-contained']]: scrollContained,
            [styles['host--space-reserved']]:
              pinned && reservePinnedContentSpace,
          },
          className
        )}
        data-testid="page-sidebar-host"
      >
        <div className={styles['content']}>{children}</div>
        {sidebar}
      </div>
    </PageSidebarHostContext.Provider>
  );
}
